import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, CreditCard, Wallet, Building2, Smartphone, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const PROVIDER_META = {
  stripe: { label: "Credit / Debit Card", icon: CreditCard, kind: "online" },
  square: { label: "Square (Card / Apple Pay / Google Pay / Cash App)", icon: Wallet, kind: "online" },
  paypal: { label: "PayPal / Venmo", icon: Wallet, kind: "online" },
  zelle: { label: "Zelle", icon: Building2, kind: "manual" },
  chime: { label: "Chime", icon: Building2, kind: "manual" },
  cash_app: { label: "Cash App", icon: Smartphone, kind: "manual" },
  venmo: { label: "Venmo", icon: Smartphone, kind: "manual" },
  apple_pay: { label: "Apple Pay (manual)", icon: Smartphone, kind: "manual" },
  google_pay: { label: "Google Pay (manual)", icon: Smartphone, kind: "manual" },
};

export default function Pay() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}/public`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
    api.get("/settings/public").then((r) => {
      const ps = r.data.providers || [];
      setProviders(ps);
      if (ps.length) setSelected(ps[0].provider_type);
    }).catch(() => {});
  }, [orderId]);

  if (order === null) return <div className="grid h-[60vh] place-items-center text-slate-500">Loading invoice…</div>;
  if (!order) return <div className="grid h-[60vh] place-items-center text-slate-500">Invoice not found.</div>;

  const isPaid = order.paid;

  const submit = async (provider, payment_metadata = {}) => {
    setBusy(true);
    try {
      const isManual = ["zelle", "chime", "cash_app", "venmo", "apple_pay", "google_pay"].includes(provider);
      const payment_provider = isManual ? `manual_${provider}` : provider;
      const r = await api.post(`/orders/${orderId}/pay`, { payment_provider, payment_metadata });
      toast.success(r.data.status === "paid" ? "Payment recorded — thank you!" : "Payment submitted, pending verification");
      setOrder({ ...order, paid: r.data.status === "paid", status: r.data.status });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  const selectedProvider = providers.find((p) => p.provider_type === selected);

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 md:px-8 md:py-16">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">Invoice</span>
      <h1 className="mt-2 font-display text-4xl font-bold text-slate-900 md:text-5xl wavy">
        {isPaid ? "Paid in full — thank you!" : "Time to settle up."}
      </h1>
      <p className="mt-2 text-slate-600">Hi {order.customer_name?.split(" ")[0] || ""}, here are the line items for your project.</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <section className="rounded-2xl border border-cyan-100 bg-white p-6 md:p-8 lg:col-span-7">
          <h2 className="font-display text-xl font-bold text-slate-900">Line items</h2>
          <ul className="mt-4 divide-y divide-slate-100">
            {order.items?.map((it, i) => (
              <li key={i} className="flex justify-between gap-4 py-3 text-sm" data-testid={`pay-line-${i}`}>
                <div>
                  <div className="font-medium text-slate-900">{it.name}</div>
                  <div className="text-xs text-slate-500">{it.quantity} × {fmt(it.price_cents)}</div>
                </div>
                <div className="font-medium text-slate-900">{fmt(it.price_cents * it.quantity)}</div>
              </li>
            ))}
          </ul>
          {order.notes && (
            <div className="mt-4 rounded-md bg-cyan-50/60 p-4 text-sm text-cyan-900">{order.notes}</div>
          )}
          <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
            <span className="text-sm text-slate-500">Total due</span>
            <span className="font-display text-2xl font-bold text-slate-900" data-testid="pay-total">{fmt(order.total_cents)}</span>
          </div>
        </section>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 rounded-2xl border border-cyan-100 bg-white p-6 md:p-7">
            <h2 className="font-display text-xl font-bold text-slate-900">Payment method</h2>
            {isPaid && (
              <div className="mt-4 rounded-xl bg-cyan-50 p-4 text-sm text-cyan-900">
                <CheckCircle2 className="mb-1 h-5 w-5" /> This invoice is paid. Receipt sent to {order.customer_email}.
              </div>
            )}
            {!isPaid && providers.length === 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">No payment options enabled yet — please contact us to settle this invoice.</div>
            )}
            {!isPaid && providers.length > 0 && (
              <>
                <div className="mt-3 grid gap-2">
                  {providers.map((p) => {
                    const meta = PROVIDER_META[p.provider_type] || { label: p.provider_type, icon: CreditCard };
                    const Icon = meta.icon;
                    const active = selected === p.provider_type;
                    return (
                      <button
                        key={p.provider_type}
                        type="button"
                        onClick={() => setSelected(p.provider_type)}
                        className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${active ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-white hover:border-cyan-300"}`}
                        data-testid={`pay-provider-${p.provider_type}-button`}
                      >
                        <Icon className={`h-4 w-4 ${active ? "text-cyan-700" : "text-slate-500"}`} />
                        <div className="text-sm">
                          <div className="font-medium text-slate-900">{p.display_name || meta.label}</div>
                          <div className="text-[11px] text-slate-500">{meta.kind === "manual" ? "Pay manually & confirm" : "Pay online"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {selectedProvider && (
                  <div className="mt-5 rounded-xl border border-cyan-100 bg-cyan-50/40 p-4">
                    {(() => {
                      const t = selectedProvider.provider_type;
                      if (["stripe", "square", "paypal"].includes(t)) {
                        const labels = { stripe: "Stripe", square: "Square", paypal: "PayPal" };
                        return (
                          <div>
                            <div className="text-sm text-slate-700">You'll pay <strong>{fmt(order.total_cents)}</strong> securely via {labels[t]}.</div>
                            <Button onClick={() => submit(t, { simulated: true })} disabled={busy} className="btn-aqua mt-4 w-full" data-testid={`pay-submit-${t}-button`}>
                              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Pay with {labels[t]}
                            </Button>
                          </div>
                        );
                      }
                      return <PayManual provider={selectedProvider} totalCents={order.total_cents} onConfirm={(meta) => submit(t, meta)} busy={busy} />;
                    })()}
                  </div>
                )}
              </>
            )}
            <p className="mt-5 text-[11px] text-slate-400">Invoice ID: <code className="font-mono">{order.id}</code></p>
            <Link to="/" className="mt-3 block text-center text-sm text-cyan-700 hover:underline" data-testid="pay-home-link">← Back to Fix It Aquatics, LLC</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PayManual({ provider, totalCents, onConfirm, busy }) {
  const [confirmed, setConfirmed] = useState(false);
  const [reference, setReference] = useState("");
  return (
    <div>
      <div className="rounded-lg bg-white p-4 ring-1 ring-cyan-100">
        <div className="text-sm text-slate-500">Send</div>
        <div className="font-display text-2xl font-bold text-slate-900">{fmt(totalCents)}</div>
        <div className="mt-2 text-sm text-slate-600">to <span className="font-mono text-cyan-700">{provider.handle || "(handle not set)"}</span> via <strong>{provider.display_name || provider.provider_type}</strong></div>
        {provider.instructions && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{provider.instructions}</p>}
      </div>
      <div className="mt-4">
        <Label>Reference / your name on the transfer (optional)</Label>
        <Input className="mt-1" value={reference} onChange={(e) => setReference(e.target.value)} data-testid="pay-manual-reference-input" />
      </div>
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" data-testid="pay-manual-confirm-checkbox" />
        I have sent {fmt(totalCents)} via {provider.display_name || provider.provider_type}.
      </label>
      <Button onClick={() => onConfirm({ handle: provider.handle, reference })} disabled={!confirmed || busy} className="btn-aqua mt-4 w-full" data-testid="pay-manual-submit-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit (pending verification)
      </Button>
    </div>
  );
}
